<template>
  <q-layout view="hHh LpR fFf">
    <q-header class="background_gradient text-white">
      <q-toolbar class="q-pl-md">
        <q-toolbar-title style="flex: none">
          <div class="cursor-pointer" @click="router.push({ name: 'Course' })">
            <IlectLogo />
          </div>
        </q-toolbar-title>

        <q-tabs stretch no-caps align="left" inline-label>
          <q-route-tab
            v-for="menuItem in topMenuList"
            :key="menuItem.path"
            :to="menuItem.path"
            :icon="menuItem.icon"
            :label="menuItem.label"
            :class="{ 'q-tab--active': route.path.includes(menuItem.path) }"
          />
        </q-tabs>
        <q-space />

        <LangSwitcher class="q-mr-sm" />

        <q-btn-dropdown v-if="AuthModule.isAuthenticated" icon="person" flat>
          <q-list>
            <q-item v-close-popup clickable :to="'/profile'">
              <q-item-section avatar>
                <q-avatar icon="badge" />
              </q-item-section>
              <q-item-section>
                {{ t("components.header.profile") }}
              </q-item-section>
            </q-item>

            <q-item v-close-popup clickable :to="'/progress'">
              <q-item-section avatar>
                <q-avatar icon="timeline" />
              </q-item-section>
              <q-item-section>
                {{ t("components.header.progress") }}
              </q-item-section>
            </q-item>

            <q-item
              v-if="AuthModule.is_staff"
              v-close-popup
              clickable
              :to="'/gke/stats'"
            >
              <q-item-section avatar>
                <q-avatar icon="hub" />
              </q-item-section>
              <q-item-section>
                {{ t("components.header.gke_stats") }}
              </q-item-section>
            </q-item>

            <q-item v-close-popup clickable :to="'/about'">
              <q-item-section avatar>
                <q-avatar icon="info" />
              </q-item-section>
              <q-item-section>
                {{ t("components.header.about") }}
              </q-item-section>
            </q-item>

            <q-item v-close-popup clickable @click="logout">
              <q-item-section avatar>
                <q-avatar icon="logout" />
              </q-item-section>
              <q-item-section>
                {{ t("common.logout") }}
              </q-item-section>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </q-toolbar>
    </q-header>

    <q-drawer
      v-if="sideMenuList.length"
      v-model="drawer"
      show-if-above
      :mini="miniState"
      class="background_gradient_vertical text-white"
      :breakpoint="500"
      :width="200"
      @mouseover="sideMenuMouseOver"
      @mouseout="sideMenuMouseOut"
    >
      <q-list clickable class="menu-list">
        <q-item
          v-for="menuItem in sideMenuList"
          :key="menuItem.path"
          v-ripple
          :class="{
            'q-router-link--active': route.path.includes(menuItem.path),
            'text-bold': true,
          }"
          padding
          :to="menuItem.path"
        >
          <q-item-section avatar no-shadow>
            <q-btn dense round flat :icon="menuItem.icon" no-shadow>
              <q-badge
                v-if="menuItem.badge"
                :label="menuItem.badge"
                floating
                color="red"
              />
            </q-btn>
          </q-item-section>

          <q-item-section>
            {{ menuItem.label }}
          </q-item-section>
          <div class="right-border"></div>
        </q-item>
      </q-list>
    </q-drawer>

    <q-page-container>
      <Breadcrumb />
      <router-view v-slot="{ Component }">
        <keep-alive include="ForumView">
          <component :is="Component" />
        </keep-alive>
      </router-view>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { useQuasar } from "quasar";
import { useRoute, useRouter } from "vue-router";
import { ref, watch } from "vue";
import { AuthModule } from "@/store/modules/auth";
import { genericErrorHandler } from "@/api/generic-axios-error-handler";
import { BreadcrumbModule } from "@/store/modules/breadcrumbs";
import Breadcrumb from "@/components/Breadcrumb.vue";
import IlectLogo from "@/components/IlectLogo.vue";
import LangSwitcher from "@/components/LangSwitcher.vue";
import { useI18n } from "vue-i18n";
import { useCourseStore } from "@/store/courseStore.js";

const $q = useQuasar();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const courseStore = useCourseStore();

const isNarrowScreen = () => $q.screen.width < 1440;
const miniState = ref(true);
const drawer = ref(false);

const topMenuList = ref([
  {
    path: "/course",
    label: t("components.header.course"),
  },
  {
    path: "/pygrade/",
    label: "PyGrade",
  },
]);

const sideMenuList = ref([]);

$q.dark.set(AuthModule.isDarkMode);
if (AuthModule.isDarkMode) {
  AuthModule.toggleDarkMode();
}

watch(
  () => route.params.course_id,
  async (courseId) => {
    if (!courseId) {
      courseStore.clearCourse();
      BreadcrumbModule.setCourseName("");
      sideMenuList.value = [];
      return;
    }

    try {
      const course = await courseStore.fetchCourse(courseId);
      if (route.params.course_id !== courseId) return;
      BreadcrumbModule.setCourseName(course.title);
      setSideMenu();
    } catch (err) {
      genericErrorHandler(err);
    }
  },
);

watch(locale, () => {
  setSideMenu();
});

const sideMenuMouseOver = () => {
  if (isNarrowScreen()) miniState.value = false;
};

const sideMenuMouseOut = () => {
  if (isNarrowScreen()) miniState.value = true;
};

// Update when screen size changes
watch(
  () => $q.screen.width,
  () => {
    miniState.value = isNarrowScreen();
  },
  { immediate: true },
);

function logout() {
  $q.dialog({
    title: t("common.confirm"),
    message: t("messages.questions.sureToLogout"),
    cancel: true,
  }).onOk(() => {
    AuthModule.resetToken();
    router.push({ name: "Login" });
  });
}

function setSideMenu() {
  const courseId = route.params.course_id;
  if (!courseId) {
    sideMenuList.value = [];
    return;
  }

  sideMenuList.value = [
    {
      icon: "fas fa-solid fa-book",
      path: `/course/${courseId}/workbook`,
      label: t("components.sideMenu.workbook"),
      visible: true,
    },
    {
      icon: "play_circle",
      path: `/course/${courseId}/video`,
      label: t("components.sideMenu.video"),
      visible: courseStore.course?.has_videos,
    },
    {
      icon: "fas fa-solid fa-calendar-day",
      path: `/course/${courseId}/calendar`,
      label: t("components.sideMenu.calendar"),
      visible: true,
    },
    {
      icon: "assignment",
      path: `/course/${courseId}/assignment`,
      label: t("components.sideMenu.assignment"),
      visible: true,
    },
    {
      icon: "quiz",
      path: `/course/${courseId}/quizzes`,
      label: t("components.sideMenu.quizzes"),
      visible: true,
    },
    {
      icon: "ballot",
      path: `/course/${courseId}/submission`,
      label: t("components.sideMenu.submission"),
      visible: true,
    },
    {
      icon: "forum",
      path: `/course/${courseId}/forum-records`,
      label: t("components.sideMenu.forum"),
      visible: true,
    },
    {
      icon: "analytics",
      path: `/course/${courseId}/assessment`,
      label: t("components.sideMenu.assessment"),
      visible: courseStore.course?.has_assessment,
    },
    {
      icon: "settings",
      path: `/course/${courseId}/settings`,
      label: t("components.sideMenu.settings"),
      visible: AuthModule.is_staff,
    },
  ];

  sideMenuList.value = sideMenuList.value.filter((item) => item.visible);
}
</script>
